import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, ArrowLeft } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";

export const WEDDINGS_CFG = {
  coll: "weddings",
  kicker: "Weddings",
  headingKey: "weddings_heading",
  introKey: "weddings_intro",
  heading: "Weddings we've had the honour of planning.",
  intro: "Every love story is different — here are a few of the celebrations we've brought to life.",
  viewLabel: "View Wedding",
  showType: false,
};
export const EVENTS_CFG = {
  coll: "events",
  kicker: "Events",
  headingKey: "events_heading",
  introKey: "events_intro",
  heading: "Events crafted for every occasion.",
  intro: "From milestones to corporate and cultural celebrations — a look at the events we've designed.",
  viewLabel: "View Event",
  showType: true,
};

function Img({ url, alt, className }) {
  if (!url) return <div className={`hero-full-placeholder ${className}`} />;
  return <img src={assetUrl(url)} alt={alt || ""} className={className} loading="lazy" />;
}

function Collage({ item }) {
  const imgs = [item.featured_image, ...(item.photos || []).map((p) => p.url)].filter(Boolean);
  const big = imgs[0];
  const s1 = imgs[1];
  const s2 = imgs[2];
  return (
    <div className="grid grid-cols-2 gap-2.5">
      <figure className="col-span-2 editorial-photo aspect-[16/10]">
        <Img url={big} alt={item.title} className="w-full h-full object-cover" />
        <span className="editorial-grade" />
      </figure>
      <figure className="editorial-photo aspect-square">
        <Img url={s1} className="w-full h-full object-cover" />
        <span className="editorial-grade" />
      </figure>
      <figure className="editorial-photo aspect-square">
        <Img url={s2} className="w-full h-full object-cover" />
        <span className="editorial-grade" />
      </figure>
    </div>
  );
}

export function PortfolioList({ cfg }) {
  const { content: c } = useContent();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const heading = c[cfg.headingKey] || cfg.heading;
  const intro = c[cfg.introKey] || cfg.intro;

  useEffect(() => {
    setLoading(true);
    api.get(`/${cfg.coll}`).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [cfg.coll]);

  return (
    <div data-testid={`${cfg.coll}-page`} className="pt-28 md:pt-32">
      <section className="container-lux py-14 md:py-20 text-center">
        <Reveal>
          <p className="overline mb-6">{cfg.kicker}</p>
          <h1 className="font-serif-lux text-4xl md:text-6xl font-light leading-[1.06] text-[#47562F] max-w-3xl mx-auto mb-7">
            {heading}
          </h1>
          <p className="text-[#4A4A4A] text-lg max-w-2xl mx-auto leading-relaxed">{intro}</p>
        </Reveal>
      </section>

      <section className="container-lux pb-24 md:pb-32">
        {loading ? (
          <div className="py-28 text-center text-[#8a8a80] tracking-[0.2em] uppercase text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-28 text-center text-[#8a8a80]">Coming soon — our {cfg.kicker.toLowerCase()} portfolio is being prepared.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-16 md:gap-y-24">
            {items.map((item, i) => (
              <Reveal key={item.id} delay={(i % 2) * 0.08}>
                <Link to={`/${cfg.coll}/${item.id}`} data-testid={`${cfg.coll}-card-${item.id}`} className="group block">
                  <Collage item={item} />
                  <div className="mt-6">
                    {cfg.showType && item.event_type && (
                      <p className="overline mb-2">{item.event_type}</p>
                    )}
                    <h3 className="font-serif-lux text-2xl md:text-3xl font-light text-[#1A1A1A] group-hover:text-[#8A9A6B] transition-colors">
                      {item.title}
                    </h3>
                    {(item.venue || item.city) && (
                      <p className="text-xs uppercase tracking-[0.2em] text-[#8A9A6B] mt-2.5">
                        {[item.venue, item.city].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {item.preview && <p className="text-[#4A4A4A] mt-3 leading-relaxed max-w-prose">{item.preview}</p>}
                    <span className="inline-flex items-center gap-2 mt-5 text-[#47562F] uppercase text-sm tracking-[0.18em] font-semibold">
                      {cfg.viewLabel} <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function PortfolioDetail({ cfg }) {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get(`/${cfg.coll}/${id}`).then((r) => { setItem(r.data); setState("ok"); }).catch(() => setState("error"));
  }, [cfg.coll, id]);

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center text-[#8a8a80] tracking-[0.2em] uppercase text-sm">Loading…</div>;
  if (state === "error" || !item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 text-center">
        <p className="text-[#4A4A4A]">This {cfg.kicker.slice(0, -1).toLowerCase()} could not be found.</p>
        <Link to={`/${cfg.coll}`} className="btn-outline-olive">Back to {cfg.kicker}</Link>
      </div>
    );
  }

  const meta = [item.date, item.venue, item.city].filter(Boolean);
  const photos = item.photos || [];
  const vendors = (item.vendors || []).filter((v) => v && v.name);
  const tlist = (item.testimonials && item.testimonials.length)
    ? item.testimonials
    : (item.testimonial_quote ? [{ quote: item.testimonial_quote, author: item.testimonial_author }] : []);

  return (
    <div data-testid={`${cfg.coll}-detail`} className="pt-28 md:pt-32">
      <section className="container-lux">
        <Link to={`/${cfg.coll}`} data-testid="detail-back-link" className="inline-flex items-center gap-2 text-[#8A9A6B] hover:text-[#47562F] uppercase text-xs tracking-[0.2em] font-semibold mb-10">
          <ArrowLeft size={15} /> {cfg.kicker}
        </Link>
      </section>

      {item.featured_image && (
        <section className="container-lux mb-12 md:mb-16">
          <figure className="editorial-photo w-full aspect-[16/9]">
            <img src={assetUrl(item.featured_image)} alt={item.title} className="w-full h-full object-cover" />
            <span className="editorial-grade" />
          </figure>
        </section>
      )}

      <section className="container-lux text-center max-w-3xl mx-auto mb-16 md:mb-20">
        {cfg.showType && item.event_type && <Reveal><p className="overline mb-4">{item.event_type}</p></Reveal>}
        <Reveal delay={0.06}>
          <h1 className="font-serif-lux text-4xl md:text-6xl font-light leading-[1.06] text-[#47562F] mb-6">{item.title}</h1>
        </Reveal>
        {meta.length > 0 && (
          <Reveal delay={0.12}>
            <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A6B] mb-8">{meta.join("  ·  ")}</p>
          </Reveal>
        )}
        {item.description && (
          <Reveal delay={0.16}>
            <p className="text-[#4A4A4A] text-lg leading-loose whitespace-pre-line">{item.description}</p>
          </Reveal>
        )}
      </section>

      {photos.length > 0 && (
        <section className="container-lux pb-20 md:pb-28">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {photos.map((p, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                className="editorial-photo aspect-[4/5] cursor-pointer" onClick={() => setActive(p)}
                data-testid={`detail-photo-${i}`}
              >
                {p.type === "video" ? (
                  <video src={assetUrl(p.url)} muted playsInline className="w-full h-full object-cover" />
                ) : (
                  <img src={assetUrl(p.url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                )}
                <span className="editorial-grade" />
              </motion.figure>
            ))}
          </div>
        </section>
      )}

      {(tlist.length > 0 || vendors.length > 0 || item.extra_info) && (
        <section className="bg-[#EDF0E7] py-20 md:py-28">
          <div className="container-lux grid md:grid-cols-2 gap-14 md:gap-20">
            {tlist.length > 0 && (
              <Reveal>
                <p className="overline mb-6">In their words</p>
                <div className="space-y-8">
                  {tlist.map((t, i) => (
                    <div key={i} className={i > 0 ? "pt-8 border-t border-[#47562F]/15" : ""} data-testid={`detail-testimonial-${i}`}>
                      <blockquote className="font-serif-lux italic font-light text-[#47562F] text-2xl md:text-3xl leading-snug mb-4">
                        “{t.quote}”
                      </blockquote>
                      {(t.author || t.role) && (
                        <p className="text-sm uppercase tracking-[0.2em] text-[#8A9A6B]">
                          — {[t.author, t.role].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
            <div className="space-y-12">
              {vendors.length > 0 && (
                <Reveal delay={0.08}>
                  <p className="overline mb-6">The team</p>
                  <ul className="space-y-3">
                    {vendors.map((v, i) => (
                      <li key={i} className="flex justify-between gap-4 border-b border-[#1A1A1A]/10 pb-3">
                        <span className="font-serif-lux text-lg text-[#1A1A1A]">{v.name}</span>
                        {v.role && <span className="text-sm text-[#8A9A6B] uppercase tracking-[0.14em] text-right">{v.role}</span>}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              )}
              {item.extra_info && (
                <Reveal delay={0.12}>
                  <p className="overline mb-6">Details</p>
                  <p className="text-[#4A4A4A] leading-relaxed whitespace-pre-line">{item.extra_info}</p>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 md:py-28 text-center">
        <Reveal>
          <Link to="/quote" className="btn-gold">Begin Your Journey</Link>
        </Reveal>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)} data-testid="detail-lightbox">
            <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setActive(null)} aria-label="Close"><X size={30} /></button>
            <motion.div initial={{ scale: 0.94 }} animate={{ scale: 1 }} exit={{ scale: 0.94 }} className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              {active.type === "video" ? (
                <video src={assetUrl(active.url)} controls autoPlay className="w-full max-h-[85vh]" />
              ) : (
                <img src={assetUrl(active.url)} alt="" className="w-full max-h-[85vh] object-contain" />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
